import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './context/AuthContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import CreateNotice from './pages/CreateNotice';
import NoticeDetail from './pages/NoticeDetail';
import EditNotice from './pages/EditNotice';
import Settings from './pages/Settings';
import ExternalNotice from './pages/ExternalNotice';

// Create theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#4caf50',
    },
  },
});

// Protected Route wrapper
const ProtectedRoute: React.FC<{ children: React.ReactElement; adminOnly?: boolean }> = ({ 
  children, 
  adminOnly = false 
}) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/" />;
  }

  return children;
};

// App Routes Component
const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      <Route 
        path="/login" 
        element={user ? <Navigate to="/" /> : <Login />} 
      />
      
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notices/new"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <CreateNotice />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notices/:id"
        element={
          <ProtectedRoute>
            <Layout>
              <NoticeDetail />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/notices/:id/edit"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <EditNotice />
            </Layout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/settings"
        element={
          <ProtectedRoute adminOnly>
            <Layout>
              <Settings />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* External route for Novus access (no auth required) */}
      <Route
        path="/external/:token"
        element={<ExternalNotice />}
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
};

// Main App Component
function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </Router>
    </ThemeProvider>
  );
}

export default App;
